import { Modal, ModalProps } from './modal';
import { ReactNode, Suspense, lazy, useState } from 'react';
import 'react18-json-view/src/style.css';

const JsonView = lazy(() => import('react18-json-view'));

export interface JsonViewModalProps<T> extends Omit<ModalProps, 'children'> {
  data: T | T[];
  collapsed?: number | boolean;
  itemCollapsed?: number | boolean;
  renderItemTitle?: (item: T, index: number) => ReactNode;
  renderItemMeta?: (item: T, index: number) => ReactNode;
}

export const JsonViewModal = <T,>({
  data,
  title = 'JSON View',
  size = 'lg',
  collapsed = false,
  itemCollapsed = true,
  renderItemTitle,
  renderItemMeta,
  ...modalProps
}: JsonViewModalProps<T>) => {
  const isArray = Array.isArray(data);
  const [expandedItemIndex, setExpandedItemIndex] = useState<number | null>(null);

  return (
    <Modal {...modalProps} title={title} size={size}>
      <div className="bg-light rounded p-4" style={{ maxHeight: '60vh', overflow: 'auto' }}>
        <Suspense fallback={<div className="text-muted">Loading...</div>}>
          {isArray ? (
            <div className="d-flex flex-column gap-3">
              {(data as T[]).map((item, index) => (
                <div key={index} className="bg-white rounded p-3 border">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="fw-semibold text-gray-700">
                      {renderItemTitle ? renderItemTitle(item, index) : `#${index + 1}`}
                    </div>
                    {renderItemMeta && <div className="text-muted fs-7">{renderItemMeta(item, index)}</div>}
                  </div>
                  <div role="button" onClick={() => setExpandedItemIndex(expandedItemIndex === index ? null : index)}>
                    <JsonView
                      src={item as object}
                      collapsed={expandedItemIndex === index ? false : itemCollapsed}
                      enableClipboard
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <JsonView src={data as object} collapsed={collapsed} enableClipboard />
          )}
        </Suspense>
      </div>
    </Modal>
  );
};
